import { parseISO, startOfYear, endOfYear, startOfMonth, endOfMonth, endOfDay, format } from 'date-fns';

/**
 * getAllTask sometimes returns an empty till. Scheduler, overlap checks, and stackPrograms need a valid end.
 * When till is missing or invalid, use end of the `since` calendar day (same-day bar).
 */
export function ensureProgramTillForDisplay(program) {
  if (!program || typeof program !== 'object' || !program.since) return program;
  const raw = program.till;
  if (raw != null && String(raw).trim() !== '') {
    const t = parseISO(String(raw));
    if (!Number.isNaN(t.getTime())) return program;
  }
  const s = parseISO(String(program.since));
  if (Number.isNaN(s.getTime())) return program;
  return {
    ...program,
    till: format(endOfDay(s), "yyyy-MM-dd'T'HH:mm:ss"),
  };
}

// Split a program into segments based on view mode:
// - Multi-year view: split by years
// - Year view (month columns): split by months
export function splitProgramByYears(program, headerData, viewMode) {
  if (!program.since || !program.till) return [program];
  
  const originalStart = parseISO(program.since);
  const originalEnd = parseISO(program.till);
  
  // Multi-year view: split by years
  if (headerData && headerData[0]?.viewMode === 'year-multi') {
    const startYear = originalStart.getFullYear();
    const endYear = originalEnd.getFullYear();
    
    // If program spans only one year, return as-is
    if (startYear === endYear) {
      return [program];
    }
    
    // Split into segments for each year
    const segments = [];
    let segmentIndex = 0;
    for (let year = startYear; year <= endYear; year++) {
      const yearStart = year === startYear ? originalStart : startOfYear(new Date(year, 0, 1));
      const yearEnd = year === endYear ? originalEnd : endOfYear(new Date(year, 0, 1));
      
      segments.push({
        ...program,
        id: `${program.id}-segment-${year}`,
        originalId: program.id,
        originalSince: program.since,
        originalTill: program.till,
        segmentYear: year,
        segmentIndex: segmentIndex++,
        isFirstSegment: year === startYear,
        isLastSegment: year === endYear,
        since: yearStart.toISOString(),
        till: yearEnd.toISOString(),
        isSegment: true,
      });
    }
    return segments;
  }
  
  // Year view with monthly columns: split by months
  if (viewMode === 'year' && headerData && headerData[0]?.viewMode === 'month') {
    const startYear = originalStart.getFullYear();
    const startMonth = originalStart.getMonth();
    const endYear = originalEnd.getFullYear();
    const endMonth = originalEnd.getMonth();
    
    // If program spans only one month, return as-is
    if (startYear === endYear && startMonth === endMonth) {
      return [program];
    }
    
    // Split into segments for each month
    const segments = [];
    let segmentIndex = 0;
    let currentDate = new Date(startYear, startMonth, 1);
    const endDate = new Date(endYear, endMonth, 1);
    
    while (currentDate <= endDate) {
      const segmentYear = currentDate.getFullYear();
      const segmentMonth = currentDate.getMonth();
      
      const monthStart = (segmentYear === startYear && segmentMonth === startMonth)
        ? originalStart
        : startOfMonth(new Date(segmentYear, segmentMonth, 1));
      
      const monthEnd = (segmentYear === endYear && segmentMonth === endMonth)
        ? originalEnd
        : endOfMonth(new Date(segmentYear, segmentMonth, 1));
      
      segments.push({
        ...program,
        id: `${program.id}-segment-${segmentYear}-${segmentMonth}`,
        originalId: program.id,
        originalSince: program.since,
        originalTill: program.till,
        segmentYear: segmentYear,
        segmentMonth: segmentMonth,
        segmentIndex: segmentIndex++,
        isFirstSegment: segmentYear === startYear && segmentMonth === startMonth,
        isLastSegment: segmentYear === endYear && segmentMonth === endMonth,
        since: monthStart.toISOString(),
        till: monthEnd.toISOString(),
        isSegment: true,
      });
      
      // Move to next month
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return segments;
  }
  
  // For other views, return as-is
  return [program];
}

// Helper function to check if two programs overlap or are in the same column
function programsOverlapOrSameColumn(item, other, headerData) {
  // Never treat segments from the same original program as overlapping
  // They're just different parts of the same program in different columns
  const itemOriginalId = item.originalId || item.id;
  const otherOriginalId = other.originalId || other.id;
  if (itemOriginalId === otherOriginalId && item.isSegment && other.isSegment) {
    return false;
  }
  
  const isYearMulti = headerData && headerData[0]?.viewMode === 'year-multi';
  const isMonthGrid = headerData && headerData[0]?.viewMode === 'month';
  
  // For multi-year view: check if same year column
  if (isYearMulti) {
    // Ensure numeric comparison - extract year from segmentYear or date
    const itemYear = item.segmentYear !== undefined 
      ? Number(item.segmentYear) 
      : new Date(item.since).getFullYear();
    const otherYear = other.segmentYear !== undefined 
      ? Number(other.segmentYear) 
      : new Date(other.since).getFullYear();
    // Strict numeric comparison to ensure correct matching
    return Number(itemYear) === Number(otherYear);
  }
  
  // For year view (month columns): check if same month column
  if (isMonthGrid) {
    // If both have segmentMonth, compare directly
    if (item.segmentMonth !== undefined && other.segmentMonth !== undefined &&
        item.segmentYear !== undefined && other.segmentYear !== undefined) {
      return item.segmentYear === other.segmentYear && item.segmentMonth === other.segmentMonth;
    }
    
    // If segments don't have segmentMonth, extract from dates
    const itemDate = new Date(item.since);
    const otherDate = new Date(other.since);
    const itemYear = item.segmentYear !== undefined ? item.segmentYear : itemDate.getFullYear();
    const itemMonth = item.segmentMonth !== undefined ? item.segmentMonth : itemDate.getMonth();
    const otherYear = other.segmentYear !== undefined ? other.segmentYear : otherDate.getFullYear();
    const otherMonth = other.segmentMonth !== undefined ? other.segmentMonth : otherDate.getMonth();
    
    return itemYear === otherYear && itemMonth === otherMonth;
  }
  
  // For other views: check time overlap
  const itemSince = new Date(item.since);
  const itemTill = new Date(item.till);
  const otherSince = new Date(other.since);
  const otherTill = new Date(other.till);
  
  // Two programs overlap if: item starts before other ends AND item ends after other starts
  return itemSince < otherTill && itemTill > otherSince;
}

export function stackPrograms(programsForChannel, headerData) {
  // Filter and sort programs
  const sorted = [...programsForChannel].filter(p => p.since && p.till).sort((a, b) => {
    // Sort by originalId first (so segments of same program are together), then by date
    const aOriginalId = a.originalId || a.id;
    const bOriginalId = b.originalId || b.id;
    if (aOriginalId !== bOriginalId) {
      return aOriginalId.localeCompare(bOriginalId);
    }
    return new Date(a.since) - new Date(b.since);
  });
  
  const withLane = [];
  const programLanes = new Map(); // Track which lane each original program uses (for segments)
  
  sorted.forEach(p => {
    const originalId = p.originalId || p.id;
    
    // For segments, use the same lane as the original program
    if (p.isSegment && programLanes.has(originalId)) {
      const laneIdx = programLanes.get(originalId);
      withLane.push({ ...p, laneIdx });
    } else {
      // ALL programs go to lane 0 (single row)
      const laneIdx = 0;
      
      // Remember this lane for segments of the same program
      if (p.isSegment) {
        programLanes.set(originalId, laneIdx);
      }
      
      withLane.push({ ...p, laneIdx });
    }
  });
  
  // Determine view mode
  const isYearMulti = headerData && headerData[0]?.viewMode === 'year-multi';
  const isMonthGrid = headerData && headerData[0]?.viewMode === 'month';

  // Sort by start date for processing
  withLane.sort((a, b) => new Date(a.since) - new Date(b.since));

  // For year-multi and month grid views: use horizontal stacking (like multi-year view)
  // Group programs by column (year for multi-year, month for year view)
  if (isYearMulti || isMonthGrid) {
    const processed = new Set();
    withLane.forEach(item => {
      if (processed.has(item.id)) return;
      
      // Find all programs that are in the same column (same year or same month)
      const sameColumnGroup = withLane.filter(other => {
        if (processed.has(other.id)) return false;
        return programsOverlapOrSameColumn(item, other, headerData);
      });
      
      // Sort by start date within the column
      sameColumnGroup.sort((a, b) => new Date(a.since) - new Date(b.since));
      
      // For the column group, find the leftmost position
      let leftmostDate;
      if (isYearMulti) {
        // Multi-year: use first day of the leftmost year
        // Ensure all years are numeric for proper comparison
        const years = sameColumnGroup.map(i => {
          if (i.segmentYear !== undefined) {
            return Number(i.segmentYear);
          }
          return new Date(i.since).getFullYear();
        });
        const minYear = Math.min(...years);
        leftmostDate = new Date(minYear, 0, 1);
      } else if (isMonthGrid) {
        // Year view: use first day of the leftmost month
        const dates = sameColumnGroup.map(i => {
          if (i.segmentMonth !== undefined && i.segmentYear !== undefined) {
            return new Date(i.segmentYear, i.segmentMonth, 1);
          }
          const d = new Date(i.since);
          return new Date(d.getFullYear(), d.getMonth(), 1);
        });
        leftmostDate = new Date(Math.min(...dates.map(d => d.getTime())));
      } else {
        leftmostDate = new Date(Math.min(...sameColumnGroup.map(i => new Date(i.since).getTime())));
      }
      
      // Assign horizontal indices for horizontal stacking within the column
      sameColumnGroup.forEach((overlapItem, idx) => {
        overlapItem.horizontalIdx = idx;
        overlapItem.overlapCount = sameColumnGroup.length;
        // Store the first day of the leftmost month (similar to multi-year storing first day of year)
        overlapItem.overlapGroupStart = leftmostDate.toISOString();
        processed.add(overlapItem.id);
      });
    });
    
    // Items not in any overlap group get default values
    withLane.forEach(item => {
      if (!processed.has(item.id)) {
        item.horizontalIdx = 0;
        item.overlapCount = 1;
      }
    });
    
    // Always return laneCount = 1 (single row per machine)
    return { items: withLane, laneCount: 1 };
  }

  // For other views: use time-based overlap detection
  const processed = new Set();
  withLane.forEach(item => {
    if (processed.has(item.id)) return;
    const overlappingGroup = withLane.filter(other => {
      if (processed.has(other.id)) return false;
      return programsOverlapOrSameColumn(item, other, headerData);
    });
    overlappingGroup.sort((a, b) => new Date(a.since) - new Date(b.since));
    const leftmostStart = new Date(Math.min(...overlappingGroup.map(i => new Date(i.since).getTime())));
    overlappingGroup.forEach((overlapItem, idx) => {
      overlapItem.horizontalIdx = idx;
      overlapItem.overlapCount = overlappingGroup.length;
      overlapItem.overlapGroupStart = leftmostStart.toISOString();
      processed.add(overlapItem.id);
    });
  });
  withLane.forEach(item => {
    if (!processed.has(item.id)) {
      item.horizontalIdx = 0;
      item.overlapCount = 1;
    }
  });
  
  // Always return laneCount = 1 (single row per machine)
  return { items: withLane, laneCount: 1 };
}

